import { defaultWorkspaceApiClient } from '../../cloud';
import type {
  ConnectCredentialInput,
  ConnectionPage,
  WorkspaceConnectionSummary,
} from './types';

export class ConnectionApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ConnectionApiError';
  }
}

async function request<T>(
  workspaceId: string,
  path: string,
  init?: RequestInit
): Promise<T> {
  const response = await defaultWorkspaceApiClient.request({
    workspaceId,
    path,
    init,
    category: 'settings',
    key: `connections:${init?.method ?? 'GET'}:${path}`,
    mode: init?.method && init.method !== 'GET' ? 'foreground' : 'background',
    priority: 'foreground',
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new ConnectionApiError(
      response.status,
      body?.message ?? `Connection request failed (${response.status})`
    );
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const connectionApi = {
  list(workspaceId: string, cursor?: string) {
    const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
    return request<ConnectionPage>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections${query}`
    );
  },
  get(workspaceId: string, connectionId: string) {
    return request<WorkspaceConnectionSummary>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections/${connectionId}`
    );
  },
  create(
    workspaceId: string,
    input: {
      serviceDefinitionId: string;
      label: string;
      credential: ConnectCredentialInput;
    }
  ) {
    return request<WorkspaceConnectionSummary>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }
    );
  },
  test(workspaceId: string, connectionId: string) {
    return request<{ status: 'connected' }>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections/${connectionId}/test`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      }
    );
  },
  update(
    workspaceId: string,
    connectionId: string,
    input: { label: string; expectedVersion: number }
  ) {
    return request<WorkspaceConnectionSummary>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections/${connectionId}`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }
    );
  },
  rotate(
    workspaceId: string,
    connectionId: string,
    input: {
      fields: Readonly<Record<string, string>>;
      expectedVersion: number;
    }
  ) {
    return request<WorkspaceConnectionSummary>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections/${connectionId}/rotate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      }
    );
  },
  remove(workspaceId: string, connectionId: string) {
    return request<void>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections/${connectionId}`,
      { method: 'DELETE' }
    );
  },
  resources(
    workspaceId: string,
    connectionId: string,
    type: string,
    cursor?: string
  ) {
    const query = new URLSearchParams({ type });
    if (cursor) query.set('cursor', cursor);
    return request<{
      items: readonly { id: string; type: string; label: string }[];
      nextCursor: string | null;
    }>(
      workspaceId,
      `/api/workspace/${workspaceId}/connections/${connectionId}/resources?${query}`
    );
  },
};
